import { MESSAGE_TYPE } from 'gcs-common/constants/MessageTypes';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import {
  getMessageBody,
  getMessageIsDeleted,
  getMessageIsForwarded,
  getMessageIsFromMe,
  getMessageQuotedAuthorId,
  getMessageQuotedDateCreated,
  getMessageType,
} from 'gcs-common/slices/messages/messageSelector';
import { getUserLoaded } from 'gcs-common/slices/user/userSelectors';
import { fetchUser } from 'gcs-common/slices/users/usersThunks/fetchUser';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import closeIcon from '../../img/close.svg';
import LinkifyBlank from '../LinkifyBlank/LinkifyBlank';
import MediaMessage from '../MediaMessage/MediaMessage';
import DeletedMessage from '../Message/DeletedMessage/DeletedMessage';
import VideoMessage from '../VideoMessage/VideoMessage';
import QuotedMessageName from './QuotedMessageName/QuotedMessageName';
import styles from './styles.module.scss';

const QuotedMessage = ({ messageId, onCancel }) => {
  const body = useSelector(getMessageBody(messageId));
  const isForwarded = useSelector(getMessageIsForwarded(messageId));
  const dateCreated = useSelector(getMessageQuotedDateCreated(messageId));
  const authorId = useSelector(getMessageQuotedAuthorId(messageId));
  const messageType = useSelector(getMessageType(messageId));
  const isDeleted = useSelector(getMessageIsDeleted(messageId));
  const isAuthorLoaded = useSelector(getUserLoaded(authorId));
  const isFromMe = useSelector(getMessageIsFromMe(messageId));
  const selectedChannelId = useSelector(getSelectedChannelId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isAuthorLoaded && !isForwarded && authorId) {
      dispatch(fetchUser({ id: authorId }));
    }
  }, [authorId, dispatch, isAuthorLoaded, isForwarded]);

  const displayDateCreated = useMemo(() => {
    return dateCreated.format('D.M.YYYY [um] HH:mm');
  }, [dateCreated]);

  const bodyComponent = useMemo(() => {
    const isMediaType = messageType === MESSAGE_TYPE.MEDIA_FROM_MAIL || messageType === MESSAGE_TYPE.MEDIA;
    const isVideo = messageType === MESSAGE_TYPE.VIDEO;

    if (isDeleted) {
      return (
        <div className={styles.deletedWrapper}>
          <DeletedMessage />
        </div>
      );
    }
    if (isMediaType) {
      return (
        <div className={styles.mediaWrapper}>
          <MediaMessage maxHeight={100} messageId={messageId} />
        </div>
      );
    }
    if (isVideo) {
      return <VideoMessage messageId={messageId} />;
    }

    return (
      <div className={styles.txtMsgBody}>
        <LinkifyBlank>{body}</LinkifyBlank>
      </div>
    );
  }, [body, isDeleted, messageId, messageType]);

  return (
    <div className={`${styles.replyDraft} ${!isFromMe ? styles.fromOther : styles.fromMe}`}>
      <div className={styles.borderWrapper}>
        <div className={styles.draftMessagePlaceholder}>
          <div className={styles.messageHeader}>
            <QuotedMessageName channelId={selectedChannelId} messageId={messageId} />
            {onCancel && (
              <div tabIndex={0} role="button" onKeyUp={onCancel} onClick={onCancel} className={styles.crossDraft}>
                <img src={closeIcon} alt="close-icon" />
              </div>
            )}
          </div>
          <div className={styles.msgText}>{bodyComponent}</div>
        </div>
        <div className={styles.messageFooter}>
          <span>{displayDateCreated}</span>
        </div>
      </div>
    </div>
  );
};

QuotedMessage.propTypes = {
  messageId: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
};

export default QuotedMessage;
